import { useCallback, useState } from "react";
import { Button, Spinner } from "@radix-ui/themes";
import { TicketExploreRow } from "@/api/useExplore/Explore.model";
import { useQualityTracking } from "../useQualityTracking";
import { TicketScore } from "./TicketQAScore.model";
import { QuestionList } from "./QuestionList";
import styles from "./TicketQAScore.module.scss";
import { useTicketValidationApi } from "@/v2/entities/ticket-validation/TicketValidation.api";
import { useMutation } from "react-query";

interface TicketQuestionsScoreProps {
  ticket: TicketExploreRow;
  ticketScore?: TicketScore[];
  onUpdateTicketScore: () => Promise<unknown>;
  allowValidateTicket?: boolean;
  viewOnly?: boolean;
  avgQAScore?: number;
}

function TicketQuestionsScore({
  ticket,
  ticketScore = [],
  onUpdateTicketScore,
  allowValidateTicket = false,
  viewOnly = false,
  avgQAScore,
}: TicketQuestionsScoreProps) {
  const ticketValidationApi = useTicketValidationApi();
  const { trackAuditPerformed } = useQualityTracking();
  const { mutate: updateTicketValidation } = useMutation({
    mutationFn: (isValidated: boolean) =>
      ticketValidationApi.updateTicketValidation({
        ticketId: ticket.id,
        isValidated,
      }),
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleValidateTicket = useCallback(async () => {
    if (ticket == null) {
      return;
    }

    setIsLoading(true);
    await updateTicketValidation(true);
    await onUpdateTicketScore();
    setIsLoading(false);
    trackAuditPerformed('ticket_validated', ticket.id);
  }, [ticket, updateTicketValidation, onUpdateTicketScore]);

  return (
    <div className={styles.Questions}>
      <QuestionList
        ticket={ticket}
        ticketScore={ticketScore}
        onUpdateTicketScore={onUpdateTicketScore}
        viewOnly={viewOnly}
        avgQAScore={avgQAScore}
      />

      {allowValidateTicket && (
        <div className={styles.Footer}>
          <Button
            size={"2"}
            variant={"solid"}
            color={"gray"}
            onClick={handleValidateTicket}
            highContrast={!isLoading}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner loading={isLoading} />
                Validating...
              </>
            ) : (
              <>Mark as Validated</>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

export default TicketQuestionsScore;
